import { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut, Settings, UserCircle2 } from 'lucide-react';
import { resolveApiAssetUrl } from '../../services/api';

function ProfileMenuButton({ onLogout, onOpenProfile, user }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  function closeAndRun(action) {
    setIsDropdownOpen(false);
    action();
  }

  return (
    <div className="relative shrink-0 self-start sm:self-auto" ref={dropdownRef}>
      <button
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
        className="flex max-w-full items-center gap-2 rounded-full border border-[#ece6df] bg-white px-2.5 py-1.5 text-left text-[#111111] shadow-[0_12px_30px_rgba(17,17,17,0.04)] transition hover:border-[#d8d1ca] hover:bg-[#fcfbf9] sm:gap-3 sm:px-3 sm:py-2"
        onClick={() => setIsDropdownOpen((currentValue) => !currentValue)}
        type="button"
      >
        {user?.avatar ? (
          <img
            alt={`${user.username} avatar`}
            className="h-8 w-8 shrink-0 rounded-full border border-[#ece6df] object-cover sm:h-9 sm:w-9"
            src={resolveApiAssetUrl(user.avatar)}
          />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5f1ec] text-[#6f6258] sm:h-9 sm:w-9">
            <UserCircle2 className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
          </div>
        )}
        <strong className="hidden max-w-[180px] truncate text-sm font-semibold text-[#111111] sm:block">
          {user.username}
        </strong>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-[#7f736b] transition-transform sm:h-4 sm:w-4 ${
            isDropdownOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isDropdownOpen ? (
        <div
          aria-label="Account menu"
          className="absolute right-0 top-[calc(100%+10px)] z-20 w-[220px] rounded-2xl border border-[#ece6df] bg-white p-2 shadow-[0_18px_40px_rgba(17,17,17,0.08)] sm:w-[240px]"
          role="menu"
        >
          <button
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-[#111111] transition hover:bg-[#faf7f3]"
            onClick={() => closeAndRun(onOpenProfile)}
            role="menuitem"
            type="button"
          >
            <Settings className="h-4 w-4 shrink-0 text-[#7f736b]" />
            <span className="truncate">Settings</span>
          </button>

          <button
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-[#111111] transition hover:bg-[#faf7f3]"
            onClick={() => closeAndRun(onLogout)}
            role="menuitem"
            type="button"
          >
            <LogOut className="h-4 w-4 shrink-0 text-[#7f736b]" />
            <span className="truncate">Logout</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default ProfileMenuButton;
